import {
  Barcode,
  BarcodeBatch,
  BarcodeBatchListener,
  BarcodeBatchSession,
  BarcodeCapture,
  barcodeCaptureLoader,
  BarcodeCaptureListener,
  BarcodeCaptureSession,
} from '@scandit/web-datacapture-barcode'
import { DataCaptureContext, FrameSourceState, type ModuleLoader } from '@scandit/web-datacapture-core'
import { labelCaptureLoader } from '@scandit/web-datacapture-label'

import { ResultItemsListStore } from './ResultItemsListStore'
import CameraStore, { TCameraStore } from './CameraStore'
import ErrorsStore, { TErrorsStore } from './ErrorsStore'
import InitialOptionsStore, { ScannerMode, TInitialOptionsStore } from './InitialOptionsStore'
import { LabelCaptureSettingsStore } from './LabelCaptureSettingsStore'
import { PerishableProductController } from './labelCapture/PerishableProductController'
import { PriceShelfLabelController } from './labelCapture/PriceShelfLabelController'
import { ShoppingReceiptController } from './labelCapture/ShoppingReceiptController'
import { SmartphoneBoxController } from './labelCapture/SmartphoneBoxController'
import { VinCodeController } from './labelCapture/VinCodeController'
import { BarcodeBatchSettingsStore, BarcodeCaptureSettingsStore, SettingsStore } from './SettingsStore'
import SingleImageModeStore, { TSingleImageModeStore } from './SingleImageModeStore'

import type EventTracker from './EventTracker'
import { LoadingStore, TLoadingStore } from './LoadingStore'
import SDKInstance, { type SDK } from './sdk'

class CaptureListener implements BarcodeCaptureListener {
  private readonly notify: (session: BarcodeCaptureSession) => void
  constructor(notify: (session: BarcodeCaptureSession) => void) {
    this.notify = notify
  }
  didScan(_barcodeCaptureMode: BarcodeCapture, session: BarcodeCaptureSession) {
    this.notify(session)
  }
}

class BatchListener implements BarcodeBatchListener {
  private readonly notify: (session: BarcodeBatchSession) => void
  constructor(notify: (session: BarcodeBatchSession) => void) {
    this.notify = notify
  }
  didUpdateSession(_barcodeBatchMode: BarcodeBatch, session: BarcodeBatchSession) {
    this.notify(session)
  }
}

function getModuleLoaders(scannerMode: ScannerMode): ModuleLoader[] {
  switch (scannerMode) {
    case ScannerMode.LabelScanning:
      return [labelCaptureLoader()]
    default:
      return [barcodeCaptureLoader({ highEndBlurryRecognition: false })]
  }
}

class AppStore {
  public sdk: SDK | undefined = undefined
  public tracker: EventTracker | undefined

  public initialOptionsStore: TInitialOptionsStore
  public settingsStore!: SettingsStore
  public labelCaptureSettingsStore?: LabelCaptureSettingsStore
  public cameraStore: TCameraStore

  public listStore?: ResultItemsListStore

  public errorsStore: TErrorsStore
  public singleImageModeStore: TSingleImageModeStore
  public loadingStore: TLoadingStore
  private readonly captureListener: CaptureListener
  private readonly batchListener: BatchListener

  constructor() {
    this.initialOptionsStore = InitialOptionsStore(this)
    this.cameraStore = CameraStore(this)
    this.errorsStore = ErrorsStore(this)
    this.singleImageModeStore = SingleImageModeStore(this)
    this.loadingStore = LoadingStore(this)

    this.captureListener = new CaptureListener((session: BarcodeCaptureSession) => {
      const barcode: Barcode | null = session.newlyRecognizedBarcode
      if (barcode) {
        this.listStore?.addFromBarcodes([barcode])
        this.tracker!.sendEvent('Web SDK', 'scan', barcode.symbology)
      }
    })
    this.batchListener = new BatchListener((session: BarcodeBatchSession) => {
      this.listStore?.addFromTrackedBarcodes(Object.values(session.addedTrackedBarcodes))
    })
  }

  async launch() {
    const scannerMode = this.initialOptionsStore.scannerMode.peek()

    if (scannerMode === ScannerMode.ExternalListBuilding || scannerMode === ScannerMode.ExternalSearchAndFind) {
      const sampleName =
        scannerMode === ScannerMode.ExternalSearchAndFind ? 'search-and-find-sample' : 'list-building-sample'
      window.open(`https://websdk6.scandit.io/public/${sampleName}/`, '_blank', 'noopener, noreferrer')
      return
    }

    if (scannerMode == null) {
      return
    }

    const initialized = await this.initializeSDK(scannerMode)
    if (!initialized) {
      return
    }

    this.initialOptionsStore.launched.value = true
    this.listStore = new ResultItemsListStore()

    const colorInvertedEnabled = this.initialOptionsStore.colorInvertedValue.peek()
    await this.configureScannerMode(scannerMode, colorInvertedEnabled)

    await this.sdk?.getCurrentMode()?.setEnabled(true)

    await this.cameraStore.fetchCameras()
    await this.cameraStore.selectDefaultCamera()
  }

  private async initializeSDK(scannerMode: ScannerMode): Promise<boolean> {
    try {
      this.initialOptionsStore.isSDKLoading.value = true
      this.loadingStore.startListening()
      const sdk = await SDKInstance.configure(getModuleLoaders(scannerMode))
      this.injectSDK(sdk)
      return true
    } catch (error: any) {
      this.errorsStore.handleError(error)
      return false
    } finally {
      this.initialOptionsStore.isSDKLoading.value = false
      this.loadingStore.stopListening()
    }
  }

  private async configureScannerMode(scannerMode: ScannerMode, colorInvertedEnabled: boolean) {
    switch (scannerMode) {
      case ScannerMode.LabelScanning:
        return this.configureLabelCapture()
      case ScannerMode.Batch:
        return this.configureBarcodeBatch(scannerMode, colorInvertedEnabled)
      default:
        return this.configureBarcodeCapture(scannerMode, colorInvertedEnabled)
    }
  }

  private async configureLabelCapture() {
    const controllerDeps = { listStore: this.listStore! }
    this.labelCaptureSettingsStore = new LabelCaptureSettingsStore({
      controllers: [
        new PerishableProductController(controllerDeps),
        new SmartphoneBoxController(controllerDeps),
        new VinCodeController(controllerDeps),
        new ShoppingReceiptController(controllerDeps),
        new PriceShelfLabelController(controllerDeps),
      ],
      reconfigureMode: async (newSettings) => {
        const reconfigured = await this.sdk?.configureLabelCaptureMode(newSettings)
        return reconfigured?.mode
      },
      onBeginEditing: () => {
        this.listStore?.removeAll()
      },
      onPresetCommitted: () => {
        this.listStore?.removeAll()
      },
    })

    const initialSettings = await this.labelCaptureSettingsStore.buildSettings()
    const result = await this.sdk?.configureLabelCaptureMode(initialSettings)
    if (!result?.mode) {
      throw new Error('Label capture mode not configured')
    }
    this.labelCaptureSettingsStore.attach(result.mode, this.sdk!.view!)
    await this.labelCaptureSettingsStore.applyOverlayForCurrentPreset()
  }

  private async configureBarcodeBatch(scannerMode: ScannerMode, colorInvertedEnabled: boolean) {
    const { mode, settings } = (await this.sdk?.configureBatchMode()) ?? {}
    if (!mode || !settings) {
      throw new Error('Batch mode not configured')
    }
    this.settingsStore = new BarcodeBatchSettingsStore({ mode, settings })
    await this.settingsStore.init(scannerMode).setColorInverted(colorInvertedEnabled).apply()
    mode.addListener(this.batchListener)
  }

  private async configureBarcodeCapture(scannerMode: ScannerMode, colorInvertedEnabled: boolean) {
    const { mode, settings } = (await this.sdk?.configureCaptureMode()) ?? {}
    if (!mode || !settings) {
      throw new Error('Capture mode not configured')
    }
    this.settingsStore = new BarcodeCaptureSettingsStore({ mode, settings })
    await this.settingsStore.init(scannerMode).setColorInverted(colorInvertedEnabled).apply()
    mode.addListener(this.captureListener)
  }

  async pauseScanning(): Promise<void> {
    if (this.labelCaptureSettingsStore) {
      await this.labelCaptureSettingsStore.pauseScanning()
    } else {
      const mode = this.sdk?.getCurrentMode()
      const frameSource = DataCaptureContext.sharedInstance.frameSource
      if (mode == null || frameSource == null) return
      await mode.setEnabled(false)
      await frameSource.switchToDesiredState(FrameSourceState.Standby)
    }
  }

  async resumeScanning(): Promise<void> {
    if (this.labelCaptureSettingsStore) {
      await this.labelCaptureSettingsStore.resumeScanning()
    } else {
      const mode = this.sdk?.getCurrentMode()
      const frameSource = DataCaptureContext.sharedInstance.frameSource
      if (mode == null || frameSource == null) return
      await mode.setEnabled(true)
      await frameSource.switchToDesiredState(FrameSourceState.On)
    }
  }

  injectSDK(sdkInstance: SDK) {
    this.sdk = sdkInstance
  }

  injectTracker(tracker: EventTracker) {
    this.tracker = tracker
  }
}

export default AppStore
